// src/components/organizer/OrganizerNotificationsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Badge,
  Alert,
  Collapse,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

const OrganizerNotificationsWidget = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'warning',
      title: 'Capacidad casi completa',
      message: 'Maratón Ciudad tiene 480/500 participantes inscritos',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      isRead: false,
      action: 'view_event',
      metadata: { eventId: 1, eventName: 'Maratón Ciudad' }
    },
    {
      id: 2,
      type: 'info',
      title: 'Recordatorio de pago',
      message: '15 participantes tienen pagos pendientes en Copa Juvenil',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      isRead: false,
      action: 'view_payments',
      metadata: { eventId: 2, pendingCount: 15 }
    },
    {
      id: 3,
      type: 'success',
      title: 'Meta alcanzada',
      message: 'Triatlón Regional superó la meta de 80 participantes',
      timestamp: new Date(Date.now() - 60 * 60 * 1000),
      isRead: true,
      action: 'celebrate',
      metadata: { eventId: 3, currentCount: 85 }
    },
    {
      id: 4,
      type: 'error',
      title: 'Problema de pago',
      message: 'Fallo en el procesamiento de 3 pagos en Carrera 5K',
      timestamp: new Date(Date.now() - 90 * 60 * 1000),
      isRead: false,
      action: 'fix_payments',
      metadata: { eventId: 4, failedPayments: 3 }
    },
    {
      id: 5,
      type: 'info',
      title: 'Evento próximo',
      message: 'Maratón Ciudad es en 11 días - Revisar checklist',
      timestamp: new Date(Date.now() - 120 * 60 * 1000),
      isRead: true,
      action: 'view_checklist',
      metadata: { eventId: 1, daysLeft: 11 }
    }
  ]);

  const [expanded, setExpanded] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const getNotificationIcon = (type) => {
    const icons = {
      warning: <WarningIcon />,
      info: <InfoIcon />,
      success: <SuccessIcon />,
      error: <ErrorIcon />
    };
    return icons[type] || <InfoIcon />;
  };

  const getNotificationColor = (type) => {
    const colors = {
      warning: 'warning',
      info: 'info',
      success: 'success',
      error: 'error'
    };
    return colors[type] || 'default';
  };

  const getActionIcon = (action) => {
    const icons = {
      view_event: <ScheduleIcon />,
      view_payments: <PaymentIcon />,
      view_checklist: <CheckCircleIcon />,
      celebrate: <SuccessIcon />,
      fix_payments: <ErrorIcon />
    };
    return icons[action] || <InfoIcon />;
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`;
    return `Hace ${Math.floor(diffInMinutes / 1440)}d`;
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const handleNotificationAction = (notification) => {
    console.log('Ejecutando acción:', notification.action, notification.metadata);
    markAsRead(notification.id);
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const displayNotifications = expanded ? notifications : notifications.slice(0, 3);

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="🔔 Notificaciones"
        subheader={`${unreadCount} sin leer`}
        action={
          <Box display="flex" gap={1} alignItems="center">
            <Badge badgeContent={unreadCount} color="error">
              <NotificationIcon />
            </Badge>
            <IconButton size="small">
              <SettingsIcon />
            </IconButton>
          </Box>
        }
      />
      
      <CardContent>
        {/* Control de notificaciones */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationsEnabled}
                onChange={(e) => setNotificationsEnabled(e.target.checked)}
                size="small"
              />
            }
            label="Notificaciones activadas"
          />
        </Box>

        {/* Lista de notificaciones */}
        <List dense>
          {displayNotifications.map((notification) => (
            <ListItem 
              key={notification.id}
              sx={{
                bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                borderRadius: 1,
                mb: 1,
                opacity: notification.isRead ? 0.7 : 1
              }}
            >
              <ListItemIcon>
                <Badge 
                  variant="dot" 
                  color={getNotificationColor(notification.type)}
                  invisible={notification.isRead}
                >
                  {React.cloneElement(
                    getNotificationIcon(notification.type),
                    { color: getNotificationColor(notification.type), fontSize: 'small' }
                  )}
                </Badge>
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography 
                      variant="body2" 
                      fontWeight={notification.isRead ? 'normal' : 'bold'}
                    >
                      {notification.title}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(notification.timestamp)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => dismissNotification(notification.id)}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {notification.message}
                    </Typography>
                    {notification.action && (
                      <Button
                        size="small"
                        startIcon={getActionIcon(notification.action)}
                        onClick={() => handleNotificationAction(notification)}
                        sx={{ mt: 0.5 }}
                      >
                        Acción
                      </Button>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {/* Botón para expandir/contraer */}
        {notifications.length > 3 && (
          <Button
            fullWidth
            onClick={() => setExpanded(!expanded)}
            startIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
            size="small"
            sx={{ mt: 1 }}
          >
            {expanded ? 'Ver menos' : `Ver ${notifications.length - 3} más`}
          </Button>
        )}

        {/* Resumen de notificaciones */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            📊 Resumen: {notifications.filter(n => n.type === 'warning').length} advertencias, {' '}
            {notifications.filter(n => n.type === 'error').length} errores, {' '}
            {notifications.filter(n => n.type === 'success').length} éxitos
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
export default OrganizerNotificationsWidget;